import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtINR, INVOICE_STATUSES, STATUS_COLORS } from "../lib/constants";
import { Modal, Input, Select } from "./Leads";
import { Plus, Receipt, AlertCircle, Mail, BellRing } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Invoices() {
  const { user } = useAuth();
  const canEmail = user?.role === "super_admin" || user?.perms?.emails?.create;
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [sendingId, setSendingId] = useState("");
  const [scanning, setScanning] = useState(false);
  const [form, setForm] = useState({
    project_id: "", invoice_number: "", invoice_type: "Advance",
    amount: 0, status: "Raised", due_date: "",
  });

  const load = async () => {
    const [i, p] = await Promise.all([api.get("/invoices"), api.get("/projects")]);
    setItems(i.data);
    setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/invoices", { ...form, amount: Number(form.amount) });
      toast.success("Invoice created");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const updatePayment = async (id, paid, total) => {
    const status = paid >= total ? "Paid" : paid > 0 ? "Partially Paid" : "Sent";
    await api.put(`/invoices/${id}`, { paid_amount: paid, status });
    load();
  };

  const updateStatus = async (id, status) => {
    await api.put(`/invoices/${id}`, { status });
    load();
  };

  const sendReminder = async (iid) => {
    setSendingId(iid);
    try {
      const { data } = await api.post(`/emails/payment-reminder/${iid}`);
      toast.success(`Reminder sent to ${data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send reminder"); }
    finally { setSendingId(""); }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/reminders/scan");
      toast.success(data.message || "Reminder scan started");
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };

  const totals = items.reduce((acc, i) => ({
    raised: acc.raised + (i.amount || 0),
    paid: acc.paid + (i.paid_amount || 0),
    outstanding: acc.outstanding + ((i.amount || 0) - (i.paid_amount || 0)),
  }), { raised: 0, paid: 0, outstanding: 0 });

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-semibold mb-2">Finance</p>
          <h1 className="text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "Bricolage Grotesque" }}>Billing & Recovery</h1>
        </div>
        <div className="flex gap-2">
          {canEmail && (
            <button onClick={runScan} disabled={scanning} data-testid="run-reminder-scan-btn" title="Runs the 90/60/30-day deadline and 7/15/30-day payment reminder scans now"
              className="flex items-center gap-2 bg-white border border-stone-300 text-[#0F2A4A] px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-stone-50 disabled:opacity-60">
              <BellRing className="w-4 h-4" /> {scanning ? "Starting..." : "Run Reminder Scan"}
            </button>
          )}
          <button onClick={() => setOpen(true)} data-testid="new-invoice-btn" className="flex items-center gap-2 bg-stone-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium">
            <Plus className="w-4 h-4" /> New Invoice
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Stat label="Total Raised" value={fmtINR(totals.raised)} color="stone" />
        <Stat label="Collected" value={fmtINR(totals.paid)} color="emerald" />
        <Stat label="Outstanding" value={fmtINR(totals.outstanding)} color="amber" />
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs uppercase tracking-wider text-stone-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Invoice</th>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-left px-4 py-3 font-medium">Project</th>
              <th className="text-right px-4 py-3 font-medium">Amount</th>
              <th className="text-right px-4 py-3 font-medium">Paid</th>
              <th className="text-left px-4 py-3 font-medium">Due</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-center px-4 py-3 font-medium">Remind</th>
            </tr>
          </thead>
          <tbody>
            {items.map((inv) => {
              const projectName = projects.find((p) => p.id === inv.project_id)?.project_name || "—";
              const overdue = inv.due_date && new Date(inv.due_date) < new Date() && inv.status !== "Paid";
              return (
                <tr key={inv.id} data-testid={`invoice-${inv.id}`} className="border-t border-stone-100 hover:bg-stone-50">
                  <td className="px-4 py-3 font-mono text-xs text-stone-700">{inv.invoice_number}</td>
                  <td className="px-4 py-3">{inv.invoice_type}</td>
                  <td className="px-4 py-3 text-stone-700">{projectName}</td>
                  <td className="px-4 py-3 text-right font-semibold">{fmtINR(inv.amount)}</td>
                  <td className="px-4 py-3 text-right">
                    <input
                      type="number"
                      value={inv.paid_amount || 0}
                      onChange={(e) => updatePayment(inv.id, Number(e.target.value), inv.amount)}
                      data-testid={`invoice-paid-${inv.id}`}
                      className="w-24 px-2 py-1 text-right text-sm bg-stone-50 border border-stone-200 rounded focus:outline-none focus:border-emerald-700"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <span className={overdue ? "text-rose-700 font-medium" : "text-stone-700"}>{inv.due_date || "—"}</span>
                      {overdue && <AlertCircle className="w-3.5 h-3.5 text-rose-600" />}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={inv.status}
                      onChange={(e) => updateStatus(inv.id, e.target.value)}
                      className={`text-xs px-2.5 py-1 rounded-full font-medium border-0 ${STATUS_COLORS[inv.status]}`}
                    >
                      {INVOICE_STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {canEmail && inv.status !== "Paid" ? (
                      <button onClick={() => sendReminder(inv.id)} disabled={sendingId === inv.id} data-testid={`send-reminder-${inv.id}`}
                        title={inv.last_reminder_at ? `Last reminded ${inv.last_reminder_at.slice(0, 10)}` : "Email payment reminder to client"}
                        className={`p-1.5 rounded-md transition disabled:opacity-50 ${overdue ? "text-rose-600 hover:bg-rose-50" : "text-stone-400 hover:bg-stone-100 hover:text-violet-600"}`}>
                        <Mail className={`w-4 h-4 ${sendingId === inv.id ? "animate-pulse" : ""}`} />
                      </button>
                    ) : <span className="text-stone-300">—</span>}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-stone-400"><Receipt className="w-8 h-8 mx-auto mb-2 opacity-40" /> No invoices yet</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <Modal title="New Invoice" onClose={() => setOpen(false)}>
          <form onSubmit={submit} className="space-y-3">
            <Select label="Project" value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} options={[{ value: "", label: "—" }, ...projects.map((p) => ({ value: p.id, label: p.project_name }))]} testid="inv-project" />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Invoice Number" value={form.invoice_number} onChange={(v) => setForm({ ...form, invoice_number: v })} required testid="inv-number" />
              <Select label="Type" value={form.invoice_type} onChange={(v) => setForm({ ...form, invoice_type: v })} options={["Advance", "Milestone", "Final", "Annual Retainer"]} testid="inv-type" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Amount (₹)" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} required testid="inv-amount" />
              <Input label="Due Date" type="date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testid="inv-due" />
            </div>
            <Select label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={INVOICE_STATUSES} testid="inv-status" />
            <button type="submit" data-testid="inv-submit" className="w-full bg-emerald-700 text-white py-2.5 rounded-lg font-medium">Create Invoice</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  const colors = {
    stone: "border-stone-300", emerald: "border-emerald-600", amber: "border-amber-500",
  };
  return (
    <div className={`bg-white border border-stone-200 rounded-xl p-6 border-l-4 ${colors[color]}`}>
      <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold text-stone-900 mt-2" style={{ fontFamily: "Bricolage Grotesque" }}>{value}</div>
    </div>
  );
}
